import React, { useEffect, useState } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import FourOpinionsExercise from './FourOpinionsExercise';
import './FourOpinionsEmbed.css';
import { useExerciseSync } from '../../context/ExerciseSyncContext';

const FourOpinionsEmbed = ({ node, updateAttributes, editor, getPos }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const exerciseIdRef = React.useRef(node.attrs.exerciseId);
  if (!exerciseIdRef.current) exerciseIdRef.current = crypto.randomUUID();
  const exerciseId = exerciseIdRef.current;
  useEffect(() => {
    if (!node.attrs.exerciseId) updateAttributes({ exerciseId });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const { myState, otherState, updateState, liveUpdateCount } = useExerciseSync(exerciseId);

  useEffect(() => {
    if (!editor) return;
    const id = requestAnimationFrame(() => {
      try {
        editor.view.updateState(editor.view.state);
        // Mobile Safari/Chrome: re-syncing the view can leave the browser
        // thinking the (still logically-selected) editable region wants the
        // on-screen keyboard, even though the user only tapped this
        // exercise's own contentEditable=false UI. Blur ONLY if the outer
        // editor root itself still has focus (not an input inside the
        // exercise's own edit mode), so the keyboard does not pop on
        // expand/collapse.
        if (typeof document !== 'undefined' && document.activeElement === editor.view.dom) {
          editor.view.dom.blur();
        }
      } catch (_) {}
    });
    return () => cancelAnimationFrame(id);
  }, [isExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mirror a REMOTE peer's expand/collapse live, without replaying stale
  // localStorage-hydrated data on every remount — see ExerciseSyncContext's
  // liveUpdateCount for why this can't just watch otherState.expanded alone.
  const lastLiveUpdateCountRef = React.useRef(liveUpdateCount);
  useEffect(() => {
    if (liveUpdateCount === lastLiveUpdateCountRef.current) return;
    lastLiveUpdateCountRef.current = liveUpdateCount;
    if (otherState.expanded !== undefined && otherState.expanded !== isExpanded) {
      setIsExpanded(otherState.expanded);
    }
  }, [liveUpdateCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const { title = 'Four Opinions', level = 'B1', topic = '', opinions = [] } = node.attrs;

  const isStudent = (() => {
    try {
      const root = editor?.view?.dom;
      const fromRoot = root?.getAttribute?.('data-student-mode');
      if (fromRoot) return fromRoot === 'true';
      const isStudentDashboard = typeof document !== 'undefined' &&
        (window.location.pathname.includes('/student-dashboard') ||
         window.location.pathname.includes('/student-profile'));
      return isStudentDashboard;
    } catch (_) { return false; }
  })();

  const handleToggle = () => {
    const next = !isExpanded;
    setIsExpanded(next);
    updateState({ expanded: next });
  };

  const handleClose = () => {
    setIsExpanded(false);
    updateState({ expanded: false });
  };

  const getLevelConfig = (lvl) => {
    const configs = {
      'A1': { color: '#4CAF50', difficulty: 'Beginner' },
      'A2': { color: '#2196F3', difficulty: 'Elementary' },
      'B1': { color: '#FF9800', difficulty: 'Intermediate' },
      'B2': { color: '#9C27B0', difficulty: 'Upper Intermediate' },
      'C1': { color: '#F44336', difficulty: 'Advanced' },
      'C2': { color: '#795548', difficulty: 'Mastery' }
    };
    return configs[lvl] || configs['B1'];
  };

  const levelConfig = getLevelConfig(level);

  const getShortTopic = (fullTopic) => {
    if (!fullTopic) return '';
    const words = fullTopic.trim().split(/\s+/);
    return words.slice(0, 7).join(' ').replace(/[.,;:!?]+$/, '');
  };

  const shortTopic = getShortTopic(topic);

  const isHomework = (() => {
    try {
      const root = editor?.view?.dom;
      const fromRoot = root?.getAttribute?.('data-tab-type');
      if (fromRoot) return fromRoot === 'homework';
      const fromBody = typeof document !== 'undefined' ? document.body.getAttribute('data-novas-current-tab-type') : null;
      return fromBody === 'homework';
    } catch (_) { return false; }
  })();

  const handleInsertAbove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const pos = typeof getPos === 'function' ? getPos() : null;
    if (editor && typeof pos === 'number') {
      editor.chain().focus().insertContentAt(pos, { type: 'paragraph' }).setTextSelection(pos + 1).run();
    }
  };

  const handleInsertBelow = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const pos = typeof getPos === 'function' ? getPos() : null;
    if (editor && typeof pos === 'number') {
      editor.chain().focus().insertContentAt(pos + node.nodeSize, { type: 'paragraph' }).setTextSelection(pos + node.nodeSize + 1).run();
    }
  };

  const handleDragStart = (e) => {
    e.target.style.opacity = '0.5';
    e.target.style.transform = 'scale(0.95)';
    e.target.style.outline = 'none';
    e.target.style.border = 'none';
    e.target.style.boxShadow = 'none';
    e.target.classList.add('dragging');
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '';
    e.target.style.transform = '';
    e.target.style.outline = '';
    e.target.style.border = '';
    e.target.style.boxShadow = '';
    e.target.classList.remove('dragging');
  };

  return (
    <NodeViewWrapper className="four-opinions-embed" contentEditable={false} draggable={true} data-drag-handle onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="embed-insert-zone top" onMouseDown={handleInsertAbove} />
      {!isExpanded ? (
        <div className="embed-preview unified-exercise-preview" draggable={true} data-drag-handle onClick={handleToggle} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div className="embed-icon four-opinions-icon-spacing">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <div className="unified-preview-main">
            <div className="unified-preview-title">Four Opinions</div>
            <div className="unified-preview-meta">
              {opinions.length} opinion{opinions.length !== 1 ? 's' : ''} · Level: {level} - {levelConfig.difficulty}
              {shortTopic && ` · ${shortTopic}`}
            </div>
          </div>
          <div className="unified-preview-actions" onClick={e => e.stopPropagation()}>
            <button
              className="unified-icon-btn"
              title="Add to homework"
              style={{ display: (isHomework || isStudent) ? 'none' : 'inline-flex' }}
              onClick={() => {
                try {
                  window.dispatchEvent(new CustomEvent('novas:add-to-homework', {
                    detail: { type: 'fourOpinions', attrs: { opinions, title, level, topic } }
                  }));
                } catch (_) {}
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="white" viewBox="0 0 16 16">
                <path d="M8.707 1.5a1 1 0 0 0-1.414 0L.646 8.146a.5.5 0 0 0 .708.708L2 8.207V13.5A1.5 1.5 0 0 0 3.5 15h4a.5.5 0 1 0 0-1h-4a.5.5 0 0 1-.5-.5V7.207l5-5 6.646 6.647a.5.5 0 0 0 .708-.708L13 5.793V2.5a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5v1.293z"/>
                <path d="M16 12.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0m-3.5-2a.5.5 0 0 0-.5.5v1h-1a.5.5 0 0 0 0 1h1v1a.5.5 0 1 0 1 0v-1h1a.5.5 0 1 0 0-1h-1v-1a.5.5 0 0 0-.5-.5"/>
              </svg>
            </button>
            <button className="unified-icon-btn" title="Delete" style={{ display: isStudent ? 'none' : 'inline-flex' }} onClick={() => editor?.chain().focus().deleteRange({ from: getPos(), to: getPos() + node.nodeSize }).run()}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="white" viewBox="0 0 16 16">
                <path d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1zm3 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5M8 5a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7A.5.5 0 0 1 8 5m3 .5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 1 0"/>
              </svg>
            </button>
          </div>
        </div>
      ) : (
        <div className="embed-expanded" draggable={true} data-drag-handle onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <FourOpinionsExercise
            opinions={opinions}
            title={title}
            level={level}
            topic={topic}
            onClose={handleClose}
            updateAttributes={updateAttributes}
            node={node}
            editor={editor}
            getPos={getPos}
            isStudent={isStudent}
            myState={myState}
            otherState={otherState}
            updateState={updateState}
          />
        </div>
      )}
      <div className="embed-insert-zone bottom" onMouseDown={handleInsertBelow} />
    </NodeViewWrapper>
  );
};

export default FourOpinionsEmbed;
